import { useState, useCallback, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useSendSharedQuoteEmail } from '@/hooks/useSharedQuotes';
import { useToast } from '@/contexts/ToastContext';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';

// ---------------------------------------------
// Types
// ---------------------------------------------

interface SharedQuoteLinkPanelProps {
  readonly shareToken: string;
  readonly customerEmail?: string | null;
}

// ---------------------------------------------
// Main component
// ---------------------------------------------

export const SharedQuoteLinkPanel = ({ shareToken, customerEmail }: SharedQuoteLinkPanelProps) => {
  const toast = useToast();
  const sendEmailMutation = useSendSharedQuoteEmail();

  const shareUrl = `${window.location.origin}/q/${shareToken}`;

  // Copy state
  const [copied, setCopied] = useState(false);

  // Email form state
  const [emailTo, setEmailTo] = useState(customerEmail ?? '');
  const [emailMessage, setEmailMessage] = useState('');

  // ---------------------------------------------
  // Copy link
  // ---------------------------------------------

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link to clipboard.');
    }
  }, [shareUrl, toast]);

  // ---------------------------------------------
  // Send email
  // ---------------------------------------------

  const handleSendEmail = async (e: FormEvent) => {
    e.preventDefault();

    const to = emailTo.trim();
    if (!to) {
      toast.error('Please enter an email address.');
      return;
    }

    try {
      await sendEmailMutation.mutateAsync({
        token: shareToken,
        to,
        customMessage: emailMessage.trim() || undefined,
      });
      toast.success(`Quote link sent to ${to}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send email';
      toast.error(msg);
    }
  };

  // ---------------------------------------------
  // Render
  // ---------------------------------------------

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      {/* --- Success banner --- */}
      <div
        style={{
          borderRadius: 'var(--radius-md)',
          backgroundColor: 'var(--color-success-50)',
          padding: 'var(--space-3) var(--space-4)',
          fontSize: 'var(--text-sm)',
          color: 'var(--color-success-600)',
          fontWeight: 'var(--font-weight-medium)',
        }}
      >
        Quote created successfully. Share the link below with your customer.
      </div>

      {/* --- Shareable Link Card --- */}
      <div
        style={{
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-default)',
          backgroundColor: 'var(--bg-surface)',
          padding: 'var(--space-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
        }}
      >
        <h2
          style={{
            fontSize: 'var(--text-base)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--text-primary)',
            margin: 0,
          }}
        >
          Shareable Link
        </h2>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}
        >
          <div
            style={{
              flex: 1,
              padding: 'var(--space-2) var(--space-3)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-default)',
              backgroundColor: 'var(--bg-surface-sunken)',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-primary)',
              fontFamily: 'monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {shareUrl}
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={handleCopy}
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {copied ? 'Copied' : 'Copy Link'}
          </Button>
        </div>
      </div>

      {/* --- Send via Email Card --- */}
      <div
        style={{
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-default)',
          backgroundColor: 'var(--bg-surface)',
          padding: 'var(--space-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
        }}
      >
        <h2
          style={{
            fontSize: 'var(--text-base)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--text-primary)',
            margin: 0,
          }}
        >
          Send via Email
        </h2>

        <form
          onSubmit={handleSendEmail}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}
        >
          <Input
            id="sq-email-to"
            label="Recipient Email"
            type="email"
            required
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            placeholder="customer@example.com"
          />

          <div>
            <label
              htmlFor="sq-email-message"
              style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text-secondary)',
              }}
            >
              Message (optional)
            </label>
            <textarea
              id="sq-email-message"
              rows={3}
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              placeholder="Add a personal note to the email"
              style={{
                marginTop: 'var(--space-1)',
                display: 'block',
                width: '100%',
                resize: 'vertical',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-default)',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-base)',
                backgroundColor: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                outline: 'none',
              }}
            />
          </div>

          <div>
            <Button type="submit" disabled={sendEmailMutation.isPending || !emailTo.trim()}>
              {sendEmailMutation.isPending ? 'Sending...' : 'Send Email'}
            </Button>
          </div>
        </form>
      </div>

      {/* --- Back link --- */}
      <div>
        <Link
          to="/admin/shared-quotes"
          style={{
            fontSize: '12px',
            color: 'var(--text-accent)',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          &larr; Back to Shared Quotes
        </Link>
      </div>
    </div>
  );
};
